import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { trackPaywallTextShown } from "@/utils/paywallTextAnalytics";
import {
  REPLACE_MEAL_PAYWALL_CTA_BACK,
  REPLACE_MEAL_PAYWALL_CTA_PRICING,
  REPLACE_MEAL_PAYWALL_CTA_TRIAL,
  REPLACE_MEAL_PAYWALL_SUBTITLE_LEAD,
  REPLACE_MEAL_PAYWALL_SUBTITLE_TAGLINE,
  REPLACE_MEAL_PAYWALL_TITLE,
} from "@/constants/replaceMealPaywallCopy";
import { cn } from "@/lib/utils";
import {
  PAYWALL_MODAL_BOTTOM_PANEL,
  PAYWALL_MODAL_CARD,
  PAYWALL_MODAL_SCROLL_TINT,
  PAYWALL_OVERLAY,
  PAYWALL_PRIMARY_CTA,
} from "@/utils/paywallBrandStyles";

export type ReplaceMealSoftPaywallModalProps = {
  open: boolean;
  onClose: () => void;
  onTryTrial: () => void;
  onOpenPricing: () => void;
  isStartingTrial?: boolean;
};

export function ReplaceMealSoftPaywallModal({
  open,
  onClose,
  onTryTrial,
  onOpenPricing,
  isStartingTrial = false,
}: ReplaceMealSoftPaywallModalProps) {
  useEffect(() => {
    if (open) {
      trackPaywallTextShown("replace_meal_soft", { surface: "replace_meal_soft" });
    }
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={cn(
            "fixed inset-0 z-[55] flex items-end sm:items-center justify-center p-0 sm:p-4",
            PAYWALL_OVERLAY,
          )}
          onClick={() => !isStartingTrial && onClose()}
        >
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 320 }}
            className={cn(
              "w-full max-w-md max-h-[100dvh] sm:max-h-[min(100dvh,720px)] flex flex-col overflow-hidden",
              PAYWALL_MODAL_CARD,
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col min-h-0 flex-1 overflow-hidden">
              <div
                className={cn(
                  "min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pt-4 pb-2 sm:px-5 sm:pt-5 space-y-3",
                  PAYWALL_MODAL_SCROLL_TINT,
                )}
              >
                <h2 className="text-lg font-semibold leading-snug text-foreground text-balance">
                  {REPLACE_MEAL_PAYWALL_TITLE}
                </h2>
                <div className="space-y-2 text-sm text-muted-foreground leading-snug">
                  <p className="text-balance">{REPLACE_MEAL_PAYWALL_SUBTITLE_LEAD}</p>
                  <p className="text-balance">{REPLACE_MEAL_PAYWALL_SUBTITLE_TAGLINE}</p>
                </div>
              </div>

              <div
                className={cn(
                  "shrink-0 flex flex-col gap-2 px-4 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-5",
                  PAYWALL_MODAL_BOTTOM_PANEL,
                )}
              >
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  className={cn("w-full h-12 text-sm font-semibold rounded-xl border-0", PAYWALL_PRIMARY_CTA)}
                  disabled={isStartingTrial}
                  onClick={onTryTrial}
                >
                  {REPLACE_MEAL_PAYWALL_CTA_TRIAL}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="w-full h-11 text-sm rounded-xl"
                  disabled={isStartingTrial}
                  onClick={onClose}
                >
                  {REPLACE_MEAL_PAYWALL_CTA_BACK}
                </Button>
                <button
                  type="button"
                  className="w-full py-2.5 text-sm font-medium text-foreground/90 hover:text-foreground underline underline-offset-2 decoration-foreground/35 hover:decoration-foreground disabled:opacity-50"
                  disabled={isStartingTrial}
                  onClick={onOpenPricing}
                >
                  {REPLACE_MEAL_PAYWALL_CTA_PRICING}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
